import { useCallback, useEffect, useRef, useState } from 'react';
import type { Driver } from 'driver.js';
import useLocalStorage from '@core/hooks/useLocalStorage';
import { useTheme } from '@ublend-npm/aulaui-core';
import { announceStep, attachSkipTourButton } from './tourA11y';
import type {
  StartTourOptions,
  TourConfig,
  TourContextValue,
  TourSource,
} from './types';

const TOUR_SCROLLING_CLASS = 'aula-tour-scrolling';
const DEFAULT_ANIMATE_MS = 700;

const waitForNextPaint = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

const suppressDriverScrollIntoView = (element: Element | undefined): void => {
  if (!(element instanceof HTMLElement)) {
    return;
  }
  element.scrollIntoView = () => undefined;
  queueMicrotask(() => {
    Reflect.deleteProperty(element, 'scrollIntoView');
  });
};

const getStepId = (step?: { data?: any }): string | undefined =>
  typeof step?.data?.stepId === 'string' ? step.data.stepId : undefined;

export type TourCompletionByVariant = {
  [variant: string]: boolean | undefined;
};

const useTour = (
  config: TourConfig,
  variant: string,
  isReady: boolean
): TourContextValue => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [completedByVariant, setCompletedByVariant] = useLocalStorage(
    config.storageKey,
    {} as TourCompletionByVariant
  ) as [
    TourCompletionByVariant,
    (
      value:
        | TourCompletionByVariant
        | ((prev: TourCompletionByVariant) => TourCompletionByVariant)
    ) => void
  ];

  const tourCompleted = Boolean(completedByVariant?.[variant]);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isTourActive, setIsTourActive] = useState(false);

  const driverRef = useRef<Driver | null>(null);
  const sourceRef = useRef<TourSource>('first_visit');
  const completedViaDoneRef = useRef(false);
  const promptTrackedForVariantRef = useRef<string | null>(null);
  const isTourActiveRef = useRef(false);
  const refreshTimeoutRef = useRef<number | null>(null);
  const isDarkModeRef = useRef(isDarkMode);
  const variantRef = useRef(variant);
  const completedByVariantRef = useRef(completedByVariant);
  const configRef = useRef(config);

  isDarkModeRef.current = isDarkMode;
  variantRef.current = variant;
  completedByVariantRef.current = completedByVariant;
  configRef.current = config;
  isTourActiveRef.current = isTourActive;

  const clearRefreshTimeout = useCallback(() => {
    if (refreshTimeoutRef.current != null) {
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  const destroyTour = useCallback(() => {
    clearRefreshTimeout();
    document.body.classList.remove(TOUR_SCROLLING_CLASS);
    if (driverRef.current) {
      try {
        driverRef.current.destroy();
      } catch {
        // Driver may already be destroyed
      }
      driverRef.current = null;
    }
    configRef.current.onAfterDestroy?.();
    setIsTourActive(false);
  }, [clearRefreshTimeout]);

  useEffect(() => () => destroyTour(), [destroyTour]);

  useEffect(() => {
    if (
      !isReady ||
      tourCompleted ||
      isTourActive ||
      promptTrackedForVariantRef.current === variant
    ) {
      return undefined;
    }

    // Claim before the async paint wait so overlapping effect runs cannot
    // double-fire analytics for the same variant.
    promptTrackedForVariantRef.current = variant;
    let cancelled = false;

    const showPrompt = async () => {
      await waitForNextPaint();
      if (
        cancelled ||
        completedByVariantRef.current?.[variant] ||
        isTourActiveRef.current
      ) {
        if (cancelled && promptTrackedForVariantRef.current === variant) {
          promptTrackedForVariantRef.current = null;
        }
        return;
      }

      setShowWelcome(true);
      configRef.current.analytics.onPromptShown?.();
    };

    showPrompt();
    return () => {
      cancelled = true;
    };
  }, [isReady, tourCompleted, variant, isTourActive]);

  const markCompleted = useCallback(
    (v: string = variantRef.current) => {
      setCompletedByVariant((prev) => {
        const next: TourCompletionByVariant = {
          ...(prev || {}),
          [v]: true,
        };
        completedByVariantRef.current = next;
        return next;
      });
      setShowWelcome(false);
    },
    [setCompletedByVariant]
  );

  const startTour = useCallback(
    async (options: StartTourOptions = {}) => {
      const { force = false, source = 'first_visit' } = options;
      const currentVariant = variantRef.current;
      const cfg = configRef.current;

      if (!force && completedByVariantRef.current?.[currentVariant]) {
        return;
      }

      if (driverRef.current?.isActive()) {
        destroyTour();
      }

      setShowWelcome(false);
      sourceRef.current = source;
      completedViaDoneRef.current = false;

      await cfg.onBeforeStart?.();
      await waitForNextPaint();

      const steps = typeof cfg.steps === 'function' ? cfg.steps() : cfg.steps;
      const { buttons, theme: tourTheme, analytics } = cfg;

      if (steps.length === 0) {
        markCompleted(currentVariant);
        return;
      }

      const [{ driver }] = await Promise.all([
        import('driver.js'),
        import('driver.js/dist/driver.css'),
        import('./tour.css'),
      ]);

      const dark = isDarkModeRef.current;
      const totalSteps = steps.length;
      const animateMs = cfg.animateMs ?? DEFAULT_ANIMATE_MS;

      const driverObj = driver({
        steps,
        animate: true,
        duration: animateMs,
        smoothScroll: false,
        allowClose: true,
        allowKeyboardControl: true,
        showProgress: true,
        progressText: cfg.progressText ?? '{{current}}/{{total}}',
        nextBtnText: buttons.next,
        prevBtnText: buttons.previous,
        doneBtnText: buttons.finish,
        disableActiveInteraction: true,
        stagePadding: 8,
        stageRadius: 12,
        popoverOffset: 16,
        overlayColor: tourTheme.overlayColor(dark),
        overlayOpacity: tourTheme.overlayOpacity(dark),
        popoverClass: tourTheme.popoverClass(dark),
        skipMissingElement: true,
        onPopoverRender: (popover, { driver: activeDriver }) => {
          attachSkipTourButton(popover, buttons.skip, () => {
            completedViaDoneRef.current = false;
            activeDriver.destroy();
          });
          popover.nextButton.setAttribute(
            'aria-label',
            popover.nextButton.innerText || buttons.next
          );
          popover.previousButton.setAttribute('aria-label', buttons.previous);
        },
        onHighlightStarted: (element, step) => {
          const stepId = getStepId(step);
          const didScroll = cfg.scrollStrategy?.(element, stepId) ?? false;
          suppressDriverScrollIntoView(element);
          if (didScroll) {
            document.body.classList.add(TOUR_SCROLLING_CLASS);
          }
        },
        onHighlighted: (element, step, { driver: activeDriver, index }) => {
          const stepId = getStepId(step);
          const stepIndex = index ?? 0;
          clearRefreshTimeout();

          refreshTimeoutRef.current = window.setTimeout(() => {
            refreshTimeoutRef.current = null;
            document.body.classList.remove(TOUR_SCROLLING_CLASS);
            cfg.onHighlighted?.({
              element,
              stepId,
              stepIndex,
              totalSteps,
              driver: { refresh: () => activeDriver.refresh() },
            });
          }, 0);

          const title = step.popover?.title ?? `${cfg.id} tour`;
          announceStep(cfg.id, title, stepIndex, totalSteps);
          analytics.onStepViewed?.({
            stepId,
            stepIndex,
            totalSteps,
            source: sourceRef.current,
          });
        },
        onDestroyStarted: (_element, _step, { driver: activeDriver }) => {
          if (activeDriver.isLastStep()) {
            completedViaDoneRef.current = true;
          }
          activeDriver.destroy();
        },
        onDestroyed: (_element, step, { index }) => {
          clearRefreshTimeout();
          document.body.classList.remove(TOUR_SCROLLING_CLASS);
          const stepId = getStepId(step);
          const stepIndex = index ?? 0;
          const finished = completedViaDoneRef.current;

          if (finished) {
            analytics.onCompleted?.({
              stepIndex,
              totalSteps,
              source: sourceRef.current,
            });
          } else {
            analytics.onSkipped?.({
              stepId,
              stepIndex,
              totalSteps,
              source: sourceRef.current,
            });
          }

          markCompleted(variantRef.current);
          cfg.onAfterDestroy?.();
          driverRef.current = null;
          setIsTourActive(false);
        },
      });

      driverRef.current = driverObj;
      setIsTourActive(true);
      analytics.onStarted?.(source);
      driverObj.drive();
    },
    [clearRefreshTimeout, destroyTour, markCompleted]
  );

  const onWelcomeStart = useCallback(() => {
    return startTour({ force: true, source: 'first_visit' });
  }, [startTour]);

  const onWelcomeDismiss = useCallback(() => {
    setShowWelcome(false);
    markCompleted();
    configRef.current.analytics.onDismissed?.();
  }, [markCompleted]);

  return {
    startTour,
    destroyTour,
    isTourActive,
    showWelcome,
    onWelcomeStart,
    onWelcomeDismiss,
  };
};

export default useTour;
